import { useState, useCallback, useRef, useEffect } from "react";
import html2canvas from "html2canvas";
import { getTodayAsYYYYMMDD, toSnakeCase } from "../../../utils/adminPanel.utils";
import { useDashboards } from "../../../Contexts/DashboardsContext";

export const useDashboardImageExport = (dashboardId: string) => {
  const imageRef = useRef<HTMLDivElement>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [id, setId] = useState('')

  const { logDashboardDownload } = useDashboards()

  useEffect(() => {
    setId(dashboardId)
  }, [dashboardId])

  const waitForDomToStabilize = (targetNode: HTMLElement, timeout = 3000, idleDelay = 300): Promise<void> => {
    return new Promise((resolve) => {
      let idleTimer: NodeJS.Timeout | null = null;
      const observer = new MutationObserver(() => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
          observer.disconnect();
          resolve();
        }, idleDelay);
      });

      observer.observe(targetNode, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });

      setTimeout(() => {
        observer.disconnect();
        if (idleTimer) clearTimeout(idleTimer);
        resolve();
      }, timeout);

      idleTimer = setTimeout(() => {
        observer.disconnect();
        resolve();
      }, idleDelay);
    });
  };

  const handleDownloadImage = useCallback(async (fileName: string) => {
    if (!imageRef.current) return;
    setIsGeneratingImage(true);

    const downloadLogged = await logDashboardDownload(id)
    
    // Don't continue with the download if we failed to log it
    if (!downloadLogged) {
      setIsGeneratingImage(false)
      return
    }

    try {
      await document.fonts.ready;
      await waitForDomToStabilize(imageRef.current);

      const canvas = await html2canvas(imageRef.current, {
        scale: 2,
        useCORS: true,
        onclone: (clonedDoc) => {
          const elementsToHide = clonedDoc.querySelectorAll(".png-hide");
          elementsToHide.forEach((el) => {
            (el as HTMLElement).style.display = "none";
          });
        }
      });

      const imgData = canvas.toDataURL("image/png");

      const link = document.createElement("a");
      link.href = imgData;
      link.download = `${toSnakeCase(fileName)}_${getTodayAsYYYYMMDD()}.png`;
      link.click();
    } catch (err) {
      console.error("Image export failed:", err);
    } finally {
      setIsGeneratingImage(false);
    }
  }, [id]);

  return {
    imageRef,
    isGeneratingImage,
    handleDownloadImage,
  };
};
